import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Star, X } from 'lucide-react';
import { NexaGlyph } from '@/components/nexa-mark/nexa-glyph';
import { usePrefersReducedMotion } from '@/components/nexa-mark/use-prefers-reduced-motion';
import { Button } from '@/components/ui/button';
import { Hint } from '@/components/ui/hint';
import { cn } from '@/lib/cn';
import { useOpenWhy } from './porque/use-open-why';

// Nexa's guide in «Operar la disrupción» — the PRESENTATION shared by every view that says
// what to do next (cancelled: views/cancelled/next-step-card.tsx; delay:
// views/delay/delay-next.tsx). Mockups 17 + 18 of the Operar canvas: a game's LOOK (the
// navy card with the Nexa mark, a progress bar, «Listo» with its «+N» and stars, the
// copilot typing), the operation's WORDS (PO 2026-10-27: never «jugar» / «jugada»).
// Flat buttons, the mark never boxed, no pointer chips (PO review). The views own what
// the step IS and what its button does; this file only draws it. Copy: `operateCancel:next.*`.

/**
 * The card's ONE filled action: flat Nexa blue on navy, white ink (5.2:1). Not the cyan —
 * it read as light blue (PO 2026-10-27: «parece celeste, queremos un azul más oscuro»).
 */
export const NEXA_FILLED = 'h-11 bg-nexa-blue font-bold text-white hover:bg-nexa-blue/90';
/** A secondary action on the navy card / bubble (outline, white ink). */
export const NEXA_QUIET =
  'h-11 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white';

/** The copilot's typing speed: characters per tick, tick length. */
const TYPE_STEP = 2;
const TYPE_MS = 24;

/** How long a finished sentence stays before a newer one replaces it: its reading time, within bounds. */
const READ_MS_PER_CHAR = 30;
const READ_MIN_MS = 1500;
const READ_MAX_MS = 4000;

function readMs(text: string): number {
  return Math.min(READ_MAX_MS, Math.max(READ_MIN_MS, text.length * READ_MS_PER_CHAR));
}

/** One thing the copilot says: its words, the voice they belong to, and the flight. */
interface IUtterance {
  text: string;
  voice: string;
  flight: string;
}

/**
 * The typewriter: the text appearing a couple of characters at a time (whole at once
 * under reduced motion). It writes the DOM node directly — no React render per tick — so
 * a typing copilot never re-renders the screen under the operator's click (a click on
 * the card while it typed was lost); only its start and end render. It types when the
 * `voice` changes; new words in a voice it already finished (the same step, new counts)
 * are written whole, in place — retyping them read as a new message each time.
 *
 * A newer sentence WAITS for the one on screen: it is never cut mid-word, and a finished
 * one stays up long enough to read. Of the sentences that arrive meanwhile only the LAST
 * is said — a burst of events is one message, not a flicker of each (PO 2026-11-03: «no
 * termina de escribir uno que ya aparece otro»).
 */
function useTypewriter(next: IUtterance): {
  ref: RefObject<HTMLSpanElement | null>;
  typing: boolean;
  said: IUtterance;
} {
  const still = usePrefersReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const [said, setSaid] = useState<IUtterance>(next);
  const [typing, setTyping] = useState(!still);
  const typed = useRef<string | null>(null);
  const readUntil = useRef(0);

  // The queue: the latest sentence takes the screen once the current one is written and read.
  useEffect(() => {
    if (next.text === said.text && next.voice === said.voice && next.flight === said.flight) return;
    if (typing) return; // runs again when the sentence on screen is finished
    // The same voice with new words is written in place — nothing to wait for.
    const wait = next.voice === said.voice ? 0 : Math.max(0, readUntil.current - Date.now());
    const id = setTimeout(() => setSaid(next), wait);
    return () => clearTimeout(id);
  }, [next.text, next.voice, next.flight, said, typing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const finished = () => {
      readUntil.current = Date.now() + readMs(said.text);
    };
    if (still || typed.current === said.voice) {
      el.textContent = said.text;
      setTyping(false);
      finished();
      return;
    }
    let n = 0;
    el.textContent = '';
    setTyping(true);
    const id = setInterval(() => {
      n = Math.min(said.text.length, n + TYPE_STEP);
      el.textContent = said.text.slice(0, n);
      if (n >= said.text.length) {
        clearInterval(id);
        typed.current = said.voice;
        setTyping(false);
        finished();
      }
    }, TYPE_MS);
    return () => clearInterval(id);
  }, [said, still]);
  return { ref, typing, said };
}

export interface INexaCardProps {
  /** «Nexa · Paso 1 de 4», or «Nexa». */
  label: string;
  /** The step's stable key (tests, analytics). */
  step: string;
  /** Chips beside the label (worth, streak…). */
  chips?: ReactNode;
  /** The bar on the right; `null` = no bar. */
  progress?: { done: number; total: number; text: string; label: string } | null;
  /** «Listo»: the operator's step just started — shown instead of the step. */
  done?: { n: number; did: string } | null;
  title: string;
  why: string;
  /** The step's action(s). */
  action?: ReactNode;
  /** Beside the title on a wide card, under it on a phone (a step's own counts). */
  aside?: ReactNode;
  /** Under everything (the steps strip). */
  footer?: ReactNode;
}

/** The navy «Lo que sigue» card. */
export function NexaCard({
  label,
  step,
  chips,
  progress,
  done,
  title,
  why,
  action,
  aside,
  footer,
}: INexaCardProps) {
  const { t } = useTranslation('operateCancel');
  const pct =
    progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;
  return (
    <section
      aria-label={t('next.region')}
      data-testid="next-step"
      data-step={step}
      className="relative shrink-0 overflow-hidden rounded-2xl bg-nexa-navy p-5 text-white shadow-lg"
    >
      <NexaGlyph
        tone="light"
        className="pointer-events-none absolute -bottom-12 -right-8 h-56 w-60 opacity-10"
      />
      <div className="relative space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p
            className="text-xs font-extrabold uppercase tracking-widest text-nexa-cyan-soft"
            data-testid="next-step-label"
          >
            {label}
          </p>
          {done ? null : chips}
          {progress && pct != null ? (
            <div className="ml-auto w-full min-w-0 sm:w-56" data-testid="next-progress">
              <p className="mb-1 text-xs text-nexa-mist">{progress.text}</p>
              <div
                role="progressbar"
                aria-label={progress.label}
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.done}
                className="h-2.5 overflow-hidden rounded-full bg-white/15"
              >
                <div
                  className="h-full rounded-full bg-nexa-cyan transition-[width] duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          ) : null}
        </div>

        {done ? (
          <div className="relative" role="status" data-testid="next-done">
            {done.n > 0 ? (
              <span
                aria-hidden
                className="absolute right-0 top-0 text-5xl font-black text-nexa-cyan motion-safe:animate-nexa-rise"
              >
                {t('next.plus', { count: done.n })}
              </span>
            ) : null}
            <p className="text-2xl font-extrabold">{t('next.done')}</p>
            <p className="text-sm text-nexa-mist">{done.did}</p>
            <p className="mt-2 flex gap-1.5" aria-hidden>
              {[0, 1, 2].map((i) => (
                <Star
                  key={i}
                  className="h-6 w-6 fill-nexa-cyan text-nexa-cyan motion-safe:animate-nexa-pop"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 space-y-3">
              <div className="space-y-1">
                <p className="text-2xl font-extrabold leading-tight" data-testid="next-step-title">
                  {title}
                </p>
                <p className="max-w-xl text-sm text-nexa-mist">{why}</p>
              </div>
              {action ? <div className="flex flex-wrap items-start gap-2">{action}</div> : null}
            </div>
            {aside ? <div className="shrink-0 xl:w-72">{aside}</div> : null}
          </div>
        )}
        {footer}
      </div>
    </section>
  );
}

/** Nexa's copilot: the mark (unboxed, a halo while it speaks), the step in a sentence, its button, «¿Por qué?» and its ✕. */
export function NexaCopilot({
  text,
  voice = text,
  flight,
  action,
  onDismiss,
}: {
  text: string;
  /** What it types anew when it changes — by default the text itself. */
  voice?: string;
  flight: string;
  action?: ReactNode;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('operateCancel');
  const { t: to } = useTranslation('operate');
  const openWhy = useOpenWhy();
  const { ref, typing, said } = useTypewriter({ text, voice, flight });
  return (
    <section
      aria-label={t('next.copilot', { flight: said.flight })}
      data-testid="next-copilot"
      className="absolute inset-x-4 bottom-4 z-20 flex items-end gap-1 sm:right-auto sm:max-w-xl"
    >
      <div
        aria-hidden
        className="relative hidden h-24 w-24 shrink-0 items-center justify-center sm:flex"
      >
        <span
          className={cn(
            'absolute inset-1 rounded-full bg-[radial-gradient(circle,rgba(5,184,236,.32)_0,rgba(5,184,236,.12)_45%,rgba(5,184,236,0)_70%)]',
            typing && 'motion-safe:animate-nexa-halo',
          )}
        />
        <NexaGlyph className="relative h-20 w-20 drop-shadow-md" />
      </div>
      <div className="min-w-0 flex-1 space-y-3 rounded-2xl rounded-bl-md border-2 border-white bg-nexa-navy p-4 text-white shadow-xl">
        <div className="flex items-start justify-between gap-2">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-nexa-cyan">
            {t('next.nexa')}
            <span className="text-[11px] font-medium normal-case tracking-normal text-nexa-mist">
              {t('next.copilotFor', { flight: said.flight })}
            </span>
          </p>
          {/* Its ✕ closes it, typing or not (PO 2026-11-03: «Después» nobody understood). */}
          <Hint body={t('next.closeCopilot')}>
            <button
              type="button"
              onClick={onDismiss}
              aria-label={t('next.closeCopilot')}
              className="-mr-2 -mt-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-nexa-mist transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nexa-cyan"
              data-testid="next-copilot-close"
            >
              <X aria-hidden className="h-5 w-5" />
            </button>
          </Hint>
        </div>
        <p className="min-h-[3rem] text-base font-semibold leading-snug" aria-live="polite">
          <span className="sr-only">{said.text}</span>
          <span aria-hidden ref={ref} />
          {typing ? (
            <span
              aria-hidden
              className="ml-0.5 inline-block h-4 w-2 animate-pulse bg-nexa-cyan align-middle"
            />
          ) : null}
        </p>
        {typing ? null : (
          <div className="flex flex-wrap items-center gap-2">
            {action}
            {/* «¿Por qué?» — the policy rules behind what Nexa is doing (`?panel=porque`) */}
            <Button
              variant="outline"
              className={cn(NEXA_QUIET, 'gap-2')}
              onClick={openWhy}
              title={to('why.openTitle')}
              aria-haspopup="dialog"
              data-testid="next-copilot-why"
            >
              <HelpCircle aria-hidden className="h-4 w-4" />
              {to('why.open')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
